/**
 * Two-panel chrome for the auth screens.
 *
 * Sign in, register, forgot-password and reset-password all shipped as the
 * same stock template — a Pexels mountain photograph and a placeholder badge.
 * They now share this shell instead, so the four screens stay in step with
 * each other and with the site.
 *
 * The form panel follows `--panel`/`--panel-fg`, so it is white with dark type
 * in the light palettes and dark with light type in the dark ones. The film
 * panel keeps its white-on-photograph treatment either way — the photography
 * is dark whatever the palette.
 */

import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useElfPalette } from '../../main/components/landing/useElfPalette';
import { route } from '../../../shared/utils';
import elfLogo from '../../../assets/elf/elfcircle-logo.png';
import authImage from '../../../assets/elf/hero-bg.webp';
import '../../main/components/landing/elf-landing.css';
import '../../main/components/landing/elf-pages.css';

export interface ElfAuthShellProps {
  /** Small mono line above the heading, e.g. `Sign in`. */
  kicker: string;
  /** The screen's heading, set in Anton. */
  title: string;
  /** One line under the heading. */
  lede?: ReactNode;
  /** Overrides the film panel's headline for screens that want their own. */
  artHeadline?: string;
  /** Overrides the film panel's supporting line. */
  artLede?: string;
  children: ReactNode;
}

export const ElfAuthShell: React.FC<ElfAuthShellProps> = ({
  kicker,
  title,
  lede,
  artHeadline = 'Where rhythm lives & creativity thrives',
  artLede = 'Enquiries, bookings and the people behind them — all in one place.',
  children,
}) => {
  // The palette without ElfShell, which would also mount the floating palette
  // bar and the consultation dialog — neither belongs on an auth screen.
  const { rootRef } = useElfPalette();

  return (
    <div ref={rootRef} className="elf elf-auth">
      <div className="elf-auth__art">
        <img className="elf-auth__img" src={authImage} alt="" />
        <span className="elf-auth__scrim" />
        <div className="elf-auth__artbody">
          <Link to={route('/')} aria-label="ELF-CIRCLE home">
            <img className="elf-auth__logo" src={elfLogo} alt="ELF-CIRCLE" />
          </Link>
          <div>
            <span className="statl elf-auth__kicker">The desk</span>
            <h1 className="elf-auth__headline">{artHeadline}</h1>
            <p className="elf-auth__artlede">{artLede}</p>
          </div>
          <span className="elf-auth__foot">© {new Date().getFullYear()} ELF-CIRCLE INC.</span>
        </div>
      </div>

      <div className="elf-auth__panel">
        <div className="elf-auth__inner">
          {/* The mark repeats here because the film panel is hidden on a phone. */}
          <Link to={route('/')} className="elf-auth__minilogo" aria-label="ELF-CIRCLE home">
            <img src={elfLogo} alt="ELF-CIRCLE" />
          </Link>

          <span className="statl elf-auth__kicker">{kicker}</span>
          <h2 className="elf-auth__title">{title}</h2>
          {lede && <p className="elf-auth__lede">{lede}</p>}

          {children}
        </div>
      </div>
    </div>
  );
};

export default ElfAuthShell;
